import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Check, UserCircle2, Plus } from "lucide-react";
import { initials } from "@/lib/format";

function getAssigneeIds(task) {
  if (Array.isArray(task.assignee_ids)) return task.assignee_ids;
  if (task.assignee_id) return [task.assignee_id];
  return [];
}

export function AvatarGroup({ task, users, max = 3, size = "sm" }) {
  const ids = getAssigneeIds(task);
  const sizeCls = size === "lg" ? "w-7 h-7 text-[10px]" : "w-5 h-5 text-[9px]";
  if (ids.length === 0) {
    return <span className="text-xs text-neutral-300 flex items-center gap-1"><UserCircle2 className="w-4 h-4" /></span>;
  }
  const userMap = Object.fromEntries(users.map((u) => [u.id, u]));
  const shown = ids.slice(0, max);
  const extra = ids.length - shown.length;
  return (
    <div className="flex items-center -space-x-1.5">
      {shown.map((id) => {
        const u = userMap[id];
        return (
          <div
            key={id}
            className={`${sizeCls} rounded-full bg-neutral-950 text-white font-bold flex items-center justify-center shrink-0 ring-2 ring-white`}
            title={u?.name || "Unknown"}
          >
            {initials(u?.name)}
          </div>
        );
      })}
      {extra > 0 && (
        <div className={`${sizeCls} rounded-full bg-neutral-200 text-neutral-700 font-bold flex items-center justify-center shrink-0 ring-2 ring-white`}>
          +{extra}
        </div>
      )}
    </div>
  );
}

export default function AssigneeInline({ task, users, canEdit, onChange }) {
  const [q, setQ] = useState("");
  const ids = getAssigneeIds(task);
  const userMap = Object.fromEntries(users.map((u) => [u.id, u]));
  const assignees = ids.map((id) => userMap[id]).filter(Boolean);

  const trigger = (
    <button
      disabled={!canEdit}
      onClick={(e) => e.stopPropagation()}
      className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
      data-testid={`inline-assignee-${task.id}`}
    >
      {assignees.length === 0 ? (
        <span className="flex items-center gap-1 text-xs text-neutral-300 hover:text-neutral-500">
          <UserCircle2 className="w-4 h-4" /><span className="text-neutral-400">Assign</span>
        </span>
      ) : (
        <>
          <AvatarGroup task={task} users={users} max={3} />
          {assignees.length === 1 && (
            <span className="text-xs truncate max-w-[100px] ml-1">{assignees[0].name}</span>
          )}
        </>
      )}
    </button>
  );

  if (!canEdit) return trigger;

  const filtered = users.filter((u) => {
    const s = q.toLowerCase();
    return !s || u.name.toLowerCase().includes(s) || u.email.toLowerCase().includes(s);
  });

  const toggle = (uid) => {
    const next = ids.includes(uid) ? ids.filter((x) => x !== uid) : [...ids, uid];
    onChange(next);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-72 p-1" align="start" onClick={(e) => e.stopPropagation()}>
        <div className="px-1 py-1">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search people…"
            className="h-8 rounded-sm text-sm"
            data-testid={`inline-assignee-search-${task.id}`}
          />
        </div>
        {ids.length > 0 && (
          <button
            onClick={(e) => { e.stopPropagation(); onChange([]); }}
            className="w-full px-2 py-1 text-left text-[11px] text-neutral-500 hover:text-red-600 hover:bg-neutral-50 rounded-sm"
            data-testid={`inline-assignee-clear-${task.id}`}
          >
            Clear all ({ids.length})
          </button>
        )}
        <div className="max-h-60 overflow-y-auto mt-1">
          {filtered.map((u) => {
            const selected = ids.includes(u.id);
            return (
              <button
                key={u.id}
                onClick={(e) => { e.stopPropagation(); toggle(u.id); }}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-neutral-100 text-left ${selected ? "bg-blue-50" : ""}`}
                data-testid={`inline-assignee-option-${u.id}`}
              >
                <div className="w-5 h-5 rounded-full bg-neutral-950 text-white text-[9px] font-bold flex items-center justify-center">{initials(u.name)}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{u.name}</div>
                  <div className="text-[10px] text-neutral-500 truncate">{u.email}</div>
                </div>
                {selected ? <Check className="w-3 h-3 text-blue-600" /> : <Plus className="w-3 h-3 text-neutral-300" />}
              </button>
            );
          })}
          {filtered.length === 0 && <div className="px-2 py-3 text-xs text-neutral-500 text-center">No matches</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
